import React, { FC, useState, useMemo, useCallback, memo } from 'react';
import Editor from '@monaco-editor/react';
import styled from 'styled-components';
import { Tabs } from '@clatter/ui';

interface MonacoEditorTabsProps {
  htmlCode: string;
  cssCode: string;
  jsCode: string;
  variablesCode?: string;
  onHtmlChange: (value: string) => void;
  onCssChange: (value: string) => void;
  onJsChange: (value: string) => void;
  onVariablesChange?: (value: string) => void;
  isLoading?: boolean;
}

const StyledEditorContainer = styled.div`
  height: 600px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr;

  & > div {
    display: grid;
    grid-template-rows: max-content 1fr;
    grid-template-columns: 1fr;
  }
`;

const StyledEditorsWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledTabContent = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: ${({ isActive }) => (isActive ? 'visible' : 'hidden')};

  .monaco-editor {
    height: 100% !important;
  }
`;

const CodeEditor: FC<{
  language: string;
  value: string;
  onChange: (value: string | undefined) => void;
  loading?: boolean;
  isActive: boolean;
}> = memo(({ language, value, onChange, loading, isActive }) => {
  return (
    <StyledTabContent isActive={isActive}>
      <Editor
        height="100%"
        width="100%"
        language={language}
        value={value}
        onChange={onChange}
        loading={loading}
        options={{
          minimap: { enabled: false },
          scrollBeyondLastLine: true,
          fontSize: 14,
          lineNumbers: 'on',
          roundedSelection: false,
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
          },
          automaticLayout: true,
          wordWrap: 'on',
          tabSize: 4,
          insertSpaces: true,
        }}
        theme="vs-dark"
      />
    </StyledTabContent>
  );
});

export const MonacoEditorTabs: FC<MonacoEditorTabsProps> = ({
  htmlCode,
  cssCode,
  jsCode,
  variablesCode = '',
  onHtmlChange,
  onCssChange,
  onJsChange,
  onVariablesChange,
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = useMemo(
    () => [{ label: 'HTML' }, { label: 'CSS' }, { label: 'JavaScript' }, { label: 'Variables' }] as const,
    []
  );

  const handleHtmlChange = useCallback(
    (value: string | undefined) => {
      if (value !== undefined) onHtmlChange(value);
    },
    [onHtmlChange]
  );

  const handleCssChange = useCallback(
    (value: string | undefined) => {
      if (value !== undefined) onCssChange(value);
    },
    [onCssChange]
  );

  const handleJsChange = useCallback(
    (value: string | undefined) => {
      if (value !== undefined) onJsChange(value);
    },
    [onJsChange]
  );

  const handleVariablesChange = useCallback(
    (value: string | undefined) => {
      if (value !== undefined && onVariablesChange) onVariablesChange(value);
    },
    [onVariablesChange]
  );

  const editorConfigs = useMemo(
    () => [
      { language: 'html', value: htmlCode, onChange: handleHtmlChange },
      { language: 'css', value: cssCode, onChange: handleCssChange },
      { language: 'javascript', value: jsCode, onChange: handleJsChange },
      { language: 'json', value: variablesCode, onChange: handleVariablesChange },
    ],
    [htmlCode, cssCode, jsCode, variablesCode, handleHtmlChange, handleCssChange, handleJsChange, handleVariablesChange]
  );

  const allEditors = useMemo(
    () => (
      <StyledEditorsWrapper>
        <CodeEditor
          key="html"
          language={editorConfigs[0].language}
          value={editorConfigs[0].value}
          onChange={editorConfigs[0].onChange}
          loading={isLoading}
          isActive={activeTab === 0}
        />
        <CodeEditor
          key="css"
          language={editorConfigs[1].language}
          value={editorConfigs[1].value}
          onChange={editorConfigs[1].onChange}
          loading={isLoading}
          isActive={activeTab === 1}
        />
        <CodeEditor
          key="javascript"
          language={editorConfigs[2].language}
          value={editorConfigs[2].value}
          onChange={editorConfigs[2].onChange}
          loading={isLoading}
          isActive={activeTab === 2}
        />
        <CodeEditor
          key="variables"
          language={editorConfigs[3].language}
          value={editorConfigs[3].value}
          onChange={editorConfigs[3].onChange}
          loading={isLoading}
          isActive={activeTab === 3}
        />
      </StyledEditorsWrapper>
    ),
    [editorConfigs, isLoading, activeTab]
  );

  const tabChildren = useMemo(() => {
    const children: Record<number, React.ReactElement> = {};
    tabs.forEach((_, index) => {
      children[index] = allEditors;
    });
    return children;
  }, [tabs, allEditors]);

  return (
    <StyledEditorContainer>
      <Tabs
        tabs={tabs as any}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onExtraContentClick={() => {}}
        children={tabChildren}
      />
    </StyledEditorContainer>
  );
};
